/** Hora escrita a mano en recepción: `hh:mm` de reloj (1–12) más AM/PM. */
import { formatoHora12 } from '../fechas';
export type Periodo = 'AM' | 'PM';

const dos = (n: number) => String(n).padStart(2, '0');

/**
 * Máscara mientras se teclea: deja solo dígitos (y un `:` ya escrito), hasta 2 + 2 dígitos.
 * Sin `:`, desde el tercer dígito pone `:` tras los dos primeros (`091` → `09:1`, `0910` → `09:10`),
 * para que se pueda seguir tecleando 13–23. Si los dos primeros no pueden ser hora (más de 23), la hora
 * es de un dígito (`93` → `9:3`, `930` → `9:30`). `completarHora` corrige `130` → `1:30` al salir del campo.
 */
export function enmascararHora(texto: string): string {
  const conPuntos = texto.indexOf(':');
  if (conPuntos >= 0) {
    const h = texto.slice(0, conPuntos).replace(/\D/g, '').slice(0, 2);
    const m = texto.slice(conPuntos + 1).replace(/\D/g, '').slice(0, 2);
    return `${h}:${m}`;
  }
  const d = texto.replace(/\D/g, '').slice(0, 4);
  if (d.length >= 2 && Number(d.slice(0, 2)) > 23) return `${d[0]}:${d.slice(1, 3)}`;
  return d.length <= 2 ? d : `${d.slice(0, 2)}:${d.slice(2)}`;
}

/** Al salir del campo: `dd:d` (tres dígitos) es una hora de un dígito con dos de minutos (`93:0` → `9:30`). */
export function completarHora(texto: string): string {
  const m = /^(\d)(\d):(\d)$/.exec(texto);
  return m ? `${m[1]}:${m[2]}${m[3]}` : texto;
}

/**
 * Texto `h:mm`/`hh:mm` y periodo → `HH:mm` en 24 h, o `null` si no es válido.
 * Las horas 13–23 ya vienen en 24 h y no dependen del periodo; las de 1–12 lo necesitan.
 */
export function aHora24(texto: string, periodo: Periodo | ''): string | null {
  const m = /^(\d{1,2}):(\d{2})$/.exec(texto.trim());
  if (!m) return null;
  const hora = Number(m[1]);
  const minuto = Number(m[2]);
  if (minuto > 59 || hora < 1 || hora > 23) return null;
  if (hora >= 13) return `${dos(hora)}:${dos(minuto)}`;
  if (!periodo) return null;
  const h24 = periodo === 'AM' ? hora % 12 : (hora % 12) + 12;
  return `${dos(h24)}:${dos(minuto)}`;
}

/** La hora 1–12 ya está completa y solo falta AM/PM: para avisarlo mientras se escribe. */
export function faltaPeriodo(texto: string, periodo: Periodo | ''): boolean {
  return !periodo && aHora24(texto, 'AM') !== null && aHora24(texto, '') === null;
}

/** `HH:mm` en 24 h → texto `hh:mm` de reloj y su periodo. */
export function desdeHora24(hora: string): { texto: string; periodo: Periodo } {
  const h = Number(hora.slice(0, 2));
  return { texto: `${dos(h % 12 || 12)}:${hora.slice(3, 5)}`, periodo: h < 12 ? 'AM' : 'PM' };
}

/** Horario en que se puede agendar: el del barbero (`nombre`) o el de la barbería (`nombre: null`). */
export interface HorarioAtencion {
  nombre: string | null;
  entrada: string;
  salida: string;
  comidaInicio: string | null;
  comidaFin: string | null;
}

/** Ayuda bajo la hora: «Kevin atiende de 10:00 a.m. a 8:00 p.m. · comida 2:00 p.m.–3:00 p.m.». */
export function textoAtencion(a: HorarioAtencion): string {
  const rango = `de ${formatoHora12(a.entrada)} a ${formatoHora12(a.salida)}`;
  if (!a.nombre) return `La barbería atiende ${rango}`;
  const comida = a.comidaInicio && a.comidaFin ? ` · comida ${formatoHora12(a.comidaInicio)}–${formatoHora12(a.comidaFin)}` : '';
  return `${a.nombre} atiende ${rango}${comida}`;
}

/** ¿La hora `HH:mm` (24 h) cae antes de la entrada o después de la salida? Misma regla que la API. */
export function fueraDeAtencion(hora: string, a: HorarioAtencion): boolean {
  return !!hora && (hora < a.entrada || hora > a.salida);
}
